<template>
  <footer class="bf">
    <div class="bf__inner">
      <!-- Newsletter -->
      <section class="bf__newsletter" aria-labelledby="bf-newsletter-title">
        <h2 id="bf-newsletter-title" class="bf__newsletter-title">Join The Journey</h2>
        <p class="bf__newsletter-subtitle">
          Saddle up for new flavors and outlaw deals. We'll only send the good stuff.
        </p>
        <form class="bf__newsletter-form" @submit.prevent="onSubscribe">
          <input
            v-model="email"
            type="email"
            class="bf__newsletter-input"
            placeholder="your@email.com"
            aria-label="Email address"
            :disabled="status === 'pending'"
            required
          />
          <BanjinoButton
            variant="primary"
            size="md"
            type="submit"
            :loading="status === 'pending'"
          >
            {{ status === 'success' ? 'Subscribed' : 'Subscribe' }}
          </BanjinoButton>
        </form>
        <p v-if="status === 'success'" class="bf__newsletter-msg bf__newsletter-msg--success">
          You're in. Watch your inbox, partner.
        </p>
        <p v-if="status === 'error'" class="bf__newsletter-msg bf__newsletter-msg--error">
          {{ errorMessage }}
        </p>
      </section>

      <!-- Columns -->
      <div class="bf__cols">
        <div class="bf__col">
          <h3 class="bf__col-title">Shop</h3>
          <ul class="bf__list">
            <li><router-link to="/products" class="bf__link">All Products</router-link></li>
            <li><router-link to="/cart" class="bf__link">Cart</router-link></li>
            <li><a href="https://banjinos.dev" class="bf__link">The Story</a></li>
          </ul>
        </div>
        <div class="bf__col">
          <h3 class="bf__col-title">Support</h3>
          <ul class="bf__list">
            <li><a href="mailto:hello@banjinos.com" class="bf__link">Contact</a></li>
            <li><a :href="`${marketingUrl}/legal/shipping`" class="bf__link">Shipping &amp; Delivery</a></li>
            <li><a :href="`${marketingUrl}/legal/refunds`" class="bf__link">Returns &amp; Refunds</a></li>
          </ul>
        </div>
        <div class="bf__col">
          <h3 class="bf__col-title">Legal</h3>
          <ul class="bf__list">
            <li><a :href="`${marketingUrl}/legal/terms`" class="bf__link">Terms of Service</a></li>
            <li><a :href="`${marketingUrl}/legal/privacy`" class="bf__link">Privacy Policy</a></li>
            <li><a :href="`${marketingUrl}/legal/preorder`" class="bf__link">Pre-Order Terms</a></li>
            <li><a :href="`${marketingUrl}/legal/refunds`" class="bf__link">Refund Policy</a></li>
            <li><a :href="`${marketingUrl}/legal/shipping`" class="bf__link">Shipping &amp; Delivery</a></li>
          </ul>
        </div>
        <div class="bf__col">
          <h3 class="bf__col-title">Follow</h3>
          <ul class="bf__list bf__list--social">
            <li><a href="https://instagram.com" class="bf__link" aria-label="Instagram">Instagram</a></li>
            <li><a href="https://twitter.com" class="bf__link" aria-label="X / Twitter">X / Twitter</a></li>
            <li><a href="https://tiktok.com" class="bf__link" aria-label="TikTok">TikTok</a></li>
          </ul>
        </div>
      </div>

      <!-- Bottom strip -->
      <div class="bf__bottom">
        <p class="bf__copy">© {{ year }} Banjino's, LLC · Crafted with care in Texas</p>
        <p class="bf__disclaimer">Please drink responsibly. Must be 21+ to purchase. Void where prohibited.</p>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { ref, computed } from 'vue'
import axios from 'axios'
import BanjinoButton from './BanjinoButton.vue'

const year = computed(() => new Date().getFullYear())

// Legal pages live on the marketing site. Pick the right host based on
// whether we're running in dev (.dev) or production (.com).
const marketingUrl = computed(() => {
  if (typeof window === 'undefined') return 'https://banjinos.com'
  return window.location.hostname.includes('.dev')
    ? 'https://banjinos.dev'
    : 'https://banjinos.com'
})
const email = ref('')
const status = ref('idle') // idle | pending | success | error
const errorMessage = ref('')

const onSubscribe = async () => {
  if (!email.value) return
  status.value = 'pending'
  errorMessage.value = ''
  try {
    // axios.defaults.baseURL is already '/api' (set in main.js), so the path
    // here is relative to that — final URL becomes /api/v1/newsletter/subscribe,
    // same-origin to whichever shop host the user is on.
    const resp = await axios.post('/v1/newsletter/subscribe', {
      email: email.value,
      source: 'dtc-footer'
    })
    if (resp.data?.success === false) {
      throw new Error(resp.data?.error || 'Subscription failed')
    }
    status.value = 'success'
    email.value = ''
  } catch (err) {
    status.value = 'error'
    errorMessage.value = err.response?.data?.error || err.message || "Couldn't subscribe right now. Try again in a sec."
  }
}
</script>

<style lang="scss" scoped>
.bf {
  background: #1f4329;
  color: #f4e5c1;
  padding: 4rem 1.5rem 2rem;
  margin-top: 6rem;

  &__inner {
    max-width: 1280px;
    margin: 0 auto;
  }

  &__newsletter {
    text-align: center;
    max-width: 600px;
    margin: 0 auto 4rem;
  }

  &__newsletter-title {
    font-family: $font-heading;
    font-size: clamp(1.75rem, 3vw, 2.25rem);
    color: #f4e5c1;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0 0 0.75rem;
  }

  &__newsletter-subtitle {
    font-family: $font-ui;
    font-size: 1rem;
    color: #d8b767;
    margin: 0 0 1.75rem;
    line-height: 1.5;
  }

  &__newsletter-form {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
  }

  &__newsletter-input {
    flex: 1;
    min-width: 240px;
    max-width: 400px;
    background: #ffffff;
    border: 2px solid #d8b767;
    border-radius: 9999px;
    padding: 0.85rem 1.5rem;
    font-family: $font-ui;
    font-size: 1rem;
    color: #2a2a2a;

    &:focus {
      outline: none;
      border-color: #EB661F;
      box-shadow: 0 0 0 4px rgba(235, 102, 31, 0.2);
    }

    &::placeholder {
      color: #999;
    }
  }

  &__newsletter-msg {
    margin: 1rem 0 0;
    font-family: $font-ui;
    font-size: 0.9rem;

    &--success { color: #a8d9b6; }
    &--error { color: #f3b3a8; }
  }

  &__cols {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 2.5rem;
    padding-top: 3rem;
    border-top: 1px solid rgba(216, 183, 103, 0.2);
  }

  &__col-title {
    font-family: $font-heading;
    font-size: 0.95rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: #ffffff;
    margin: 0 0 1rem;
  }

  &__list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
  }

  &__link {
    font-family: $font-ui;
    font-size: 0.9rem;
    color: #d8b767;
    text-decoration: none;
    transition: color 150ms ease;

    &:hover {
      color: #ffffff;
      text-decoration: underline;
      text-underline-offset: 4px;
    }
  }

  &__bottom {
    margin-top: 3rem;
    padding-top: 2rem;
    border-top: 1px solid rgba(216, 183, 103, 0.2);
    text-align: center;
  }

  &__copy {
    font-family: $font-ui;
    font-size: 0.85rem;
    color: #d8b767;
    margin: 0 0 0.5rem;
  }

  &__disclaimer {
    font-family: $font-ui;
    font-size: 0.75rem;
    color: rgba(216, 183, 103, 0.6);
    margin: 0;
    line-height: 1.5;
  }
}
</style>
